import type { KeyboardShortcut, UserShortcuts } from '~/composables/ui/useUserPreferences'
import { isApplePlatform } from '~/utils/isApplePlatform'

export type ShortcutActionId = 'toolboxToggle' | 'geolocate'

function normalizeKey(key: string): string {
  return key.trim().toLowerCase()
}

export function shortcutMatchesEvent(shortcut: KeyboardShortcut, event: KeyboardEvent): boolean {
  const target = event.target as HTMLElement | null
  if (target?.closest?.('input, textarea, [contenteditable="true"]')) {
    return false
  }
  if (event.key === 'Dead') {
    return false
  }
  return (
    normalizeKey(event.key ?? '') === normalizeKey(shortcut.key) &&
    event.metaKey === shortcut.meta &&
    event.ctrlKey === shortcut.ctrl &&
    event.shiftKey === shortcut.shift &&
    event.altKey === shortcut.alt
  )
}

export function displayKeysForShortcut(shortcut: KeyboardShortcut): string[] {
  const isMac = isApplePlatform()
  const keys: string[] = []

  if (shortcut.meta) {
    keys.push(isMac ? '⌘' : 'Meta')
  }
  if (shortcut.ctrl) {
    keys.push(isMac ? 'Ctrl' : 'Ctrl')
  }
  if (shortcut.alt) {
    keys.push(isMac ? '⌥' : 'Alt')
  }
  if (shortcut.shift) {
    keys.push('⇧')
  }
  keys.push(shortcut.key.toUpperCase())
  return keys
}

export function useUserShortcuts(params: {
  shortcuts: Ref<UserShortcuts>
  onAction: (actionId: ShortcutActionId) => void
  /** Si false, les raccourcis boîte à outils / Me situer ne sont pas déclenchés (ex. paramètres, admin, modale). */
  enabled?: Ref<boolean>
}) {
  const active = ref(true)

  function onKeydown(event: KeyboardEvent): void {
    if (!active.value) {
      return
    }
    if (params.enabled !== undefined && !params.enabled.value) {
      return
    }
    const shortcuts = params.shortcuts.value
    const toolboxShortcut = shortcuts.toolboxToggle
    if (toolboxShortcut && shortcutMatchesEvent(toolboxShortcut, event)) {
      event.preventDefault()
      params.onAction('toolboxToggle')
      return
    }
    const geolocateShortcut = shortcuts.geolocate
    if (geolocateShortcut && shortcutMatchesEvent(geolocateShortcut, event)) {
      event.preventDefault()
      params.onAction('geolocate')
    }
  }

  onMounted(() => {
    document.addEventListener('keydown', onKeydown)
  })

  onBeforeUnmount(() => {
    document.removeEventListener('keydown', onKeydown)
  })

  return {
    active,
  }
}

